import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Music, Plus, Trash2, Copy, Headphones, GripVertical } from 'lucide-react';
import { toast } from 'sonner';

// Sortable Row Component
const SortableRow = ({ id, children, className = '' }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={`${className} ${isDragging ? 'bg-muted/50' : ''}`}
    >
      <TableCell className="w-8 p-1">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </button>
      </TableCell>
      {children}
    </TableRow>
  );
};

const EditableAudioTab = ({ event, onUpdate }) => {
  const [audioOverview, setAudioOverview] = useState(event?.audio_overview || '');
  const [audioNotes, setAudioNotes] = useState(event?.audio_notes || '');
  const [audioInputs, setAudioInputs] = useState(event?.audio_inputs || []);
  const [audioOutputs, setAudioOutputs] = useState(event?.audio_outputs || []);
  const [audioIntercoms, setAudioIntercoms] = useState(event?.audio_intercoms || []);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: null, index: null, item: null });

  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setAudioOverview(event?.audio_overview || '');
      setAudioNotes(event?.audio_notes || '');
      // Ensure all items have IDs for drag-and-drop, filter out null items
      const ensureIds = (items, prefix) => (items || [])
        .filter(item => item != null)
        .map((item, idx) => ({
          ...item,
          id: item.id || `${prefix}-${Date.now()}-${idx}`
        }));
      setAudioInputs(ensureIds(event?.audio_inputs, 'input'));
      setAudioOutputs(ensureIds(event?.audio_outputs, 'output'));
      setAudioIntercoms(ensureIds(event?.audio_intercoms, 'intercom'));
      isInitialMount.current = false;
    }
  }, [event?.audio_overview, event?.audio_notes, event?.audio_inputs, event?.audio_outputs, event?.audio_intercoms]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-number items after reorder
  const renumberItems = (items, numberField) => {
    return items.map((item, index) => ({
      ...item,
      [numberField]: String(index + 1),
    }));
  };

  // Handle drag end for inputs
  const handleInputDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      // Filter to only items with IDs (same as what's displayed)
      const validInputs = audioInputs.filter(item => item?.id);
      const oldIndex = validInputs.findIndex(item => item.id === active.id);
      const newIndex = validInputs.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validInputs, oldIndex, newIndex), 'input_number');
        setAudioInputs(reordered);
        queueChange({ audio_inputs: reordered });
        saveNow();
      }
    }
  };

  // Handle drag end for outputs
  const handleOutputDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const validOutputs = audioOutputs.filter(item => item?.id);
      const oldIndex = validOutputs.findIndex(item => item.id === active.id);
      const newIndex = validOutputs.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validOutputs, oldIndex, newIndex), 'output_number');
        setAudioOutputs(reordered);
        queueChange({ audio_outputs: reordered });
        saveNow();
      }
    }
  };

  // Handle drag end for intercoms
  const handleIntercomDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const validIntercoms = audioIntercoms.filter(item => item?.id);
      const oldIndex = validIntercoms.findIndex(item => item.id === active.id);
      const newIndex = validIntercoms.findIndex(item => item.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1) {
        const reordered = renumberItems(arrayMove(validIntercoms, oldIndex, newIndex), 'com_number');
        setAudioIntercoms(reordered);
        queueChange({ audio_intercoms: reordered });
        saveNow();
      }
    }
  };

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change
  const queueChange = useCallback((updates) => {
    pendingChanges.current = { ...pendingChanges.current, ...updates };
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleOverviewChange = (value) => {
    setAudioOverview(value);
    queueChange({ audio_overview: value });
  };

  const handleNotesChange = (value) => {
    setAudioNotes(value);
    queueChange({ audio_notes: value });
  };

  // Audio Inputs
  const addAudioInput = () => {
    const newItem = { 
      id: `input-${Date.now()}`, 
      input_number: String(audioInputs.length + 1), 
      description: '', 
      location: '', 
      notes: '' 
    };
    const newInputs = [...audioInputs, newItem];
    setAudioInputs(newInputs);
    queueChange({ audio_inputs: newInputs });
  };

  const updateAudioInput = (index, field, value) => {
    const newInputs = [...audioInputs];
    newInputs[index] = { ...newInputs[index], [field]: value };
    setAudioInputs(newInputs);
    queueChange({ audio_inputs: newInputs });
  };

  const removeAudioInput = (index) => {
    const newInputs = audioInputs.filter((_, i) => i !== index);
    // Renumber after removal
    const renumbered = newInputs.map((item, i) => ({ ...item, input_number: String(i + 1) }));
    setAudioInputs(renumbered);
    queueChange({ audio_inputs: renumbered });
  };

  const copyAudioInput = (index) => {
    const itemToCopy = audioInputs[index];
    const newItem = {
      ...itemToCopy,
      id: `input-${Date.now()}`,
      input_number: String(index + 2),
    };
    const newInputs = [...audioInputs];
    newInputs.splice(index + 1, 0, newItem);
    // Renumber all items
    const renumbered = newInputs.map((item, i) => ({ ...item, input_number: String(i + 1) }));
    setAudioInputs(renumbered);
    queueChange({ audio_inputs: renumbered });
    toast.success('Input copied');
  };

  // Audio Outputs
  const addAudioOutput = () => {
    const newItem = {
      id: `output-${Date.now()}`,
      output_number: String(audioOutputs.length + 1),
      description: '',
      location: '',
      notes: ''
    };
    const newOutputs = [...audioOutputs, newItem];
    setAudioOutputs(newOutputs);
    queueChange({ audio_outputs: newOutputs });
  };

  const updateAudioOutput = (index, field, value) => {
    const newOutputs = [...audioOutputs];
    newOutputs[index] = { ...newOutputs[index], [field]: value };
    setAudioOutputs(newOutputs);
    queueChange({ audio_outputs: newOutputs });
  };

  const copyAudioOutput = (index) => {
    const itemToCopy = audioOutputs[index];
    const newItem = {
      ...itemToCopy,
      id: `output-${Date.now()}`,
      output_number: String(index + 2),
    };
    const newOutputs = [...audioOutputs];
    newOutputs.splice(index + 1, 0, newItem);
    // Renumber all items
    const renumbered = newOutputs.map((item, i) => ({ ...item, output_number: String(i + 1) }));
    setAudioOutputs(renumbered);
    queueChange({ audio_outputs: renumbered });
    toast.success('Output copied');
  };

  const removeAudioOutput = (index) => {
    const newOutputs = audioOutputs.filter((_, i) => i !== index);
    // Renumber after removal
    const renumbered = newOutputs.map((item, i) => ({ ...item, output_number: String(i + 1) }));
    setAudioOutputs(renumbered);
    queueChange({ audio_outputs: renumbered });
  };

  // Audio Intercoms
  const addAudioIntercom = () => {
    const newItem = {
      id: `intercom-${Date.now()}`,
      com_number: String(audioIntercoms.length + 1),
      user: '',
      type: '',
      location: '',
      channels: '',
      notes: ''
    };
    const newIntercoms = [...audioIntercoms, newItem];
    setAudioIntercoms(newIntercoms);
    queueChange({ audio_intercoms: newIntercoms });
  };

  const updateAudioIntercom = (index, field, value) => {
    const newIntercoms = [...audioIntercoms];
    newIntercoms[index] = { ...newIntercoms[index], [field]: value };
    setAudioIntercoms(newIntercoms);
    queueChange({ audio_intercoms: newIntercoms });
  };

  const copyAudioIntercom = (index) => {
    const itemToCopy = audioIntercoms[index];
    const newItem = {
      ...itemToCopy,
      id: `intercom-${Date.now()}`,
      com_number: String(index + 2),
    };
    const newIntercoms = [...audioIntercoms];
    newIntercoms.splice(index + 1, 0, newItem);
    // Renumber all items
    const renumbered = newIntercoms.map((item, i) => ({ ...item, com_number: String(i + 1) }));
    setAudioIntercoms(renumbered);
    queueChange({ audio_intercoms: renumbered });
    toast.success('Intercom copied');
  };

  const removeAudioIntercom = (index) => {
    const newIntercoms = audioIntercoms.filter((_, i) => i !== index);
    // Renumber after removal
    const renumbered = newIntercoms.map((item, i) => ({ ...item, com_number: String(i + 1) }));
    setAudioIntercoms(renumbered);
    queueChange({ audio_intercoms: renumbered });
  };

  // Assign stable IDs to items that don't have them (only on state change, not every render)
  useEffect(() => {
    if (!audioInputs || audioInputs.length === 0) return;
    let needsUpdate = false;
    const updatedInputs = audioInputs.map((item, i) => {
      if (!item) return { id: `input-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      if (!item.id) {
        needsUpdate = true;
        return { ...item, id: `input-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
    if (needsUpdate) setAudioInputs(updatedInputs);
  }, [audioInputs.length]);

  useEffect(() => {
    if (!audioOutputs || audioOutputs.length === 0) return;
    let needsUpdate = false;
    const updatedOutputs = audioOutputs.map((item, i) => {
      if (!item) return { id: `output-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      if (!item.id) {
        needsUpdate = true;
        return { ...item, id: `output-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
    if (needsUpdate) setAudioOutputs(updatedOutputs);
  }, [audioOutputs.length]);

  useEffect(() => {
    if (!audioIntercoms || audioIntercoms.length === 0) return;
    let needsUpdate = false;
    const updatedIntercoms = audioIntercoms.map((item, i) => {
      if (!item) return { id: `intercom-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      if (!item.id) {
        needsUpdate = true;
        return { ...item, id: `intercom-${i}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` };
      }
      return item;
    });
    if (needsUpdate) setAudioIntercoms(updatedIntercoms);
  }, [audioIntercoms.length]);

  const confirmDelete = (type, index) => {
    let item;
    if (type === 'input') {
      item = audioInputs[index];
    } else if (type === 'output') {
      item = audioOutputs[index];
    } else if (type === 'intercom') {
      item = audioIntercoms[index];
    }
    setDeleteConfirm({ open: true, type, index, item });
  };

  const removeItem = () => {
    if (deleteConfirm.index === null) return;
    
    if (deleteConfirm.type === 'input') {
      removeAudioInput(deleteConfirm.index);
    } else if (deleteConfirm.type === 'output') {
      removeAudioOutput(deleteConfirm.index);
    } else if (deleteConfirm.type === 'intercom') {
      removeAudioIntercom(deleteConfirm.index);
    }
    
    setDeleteConfirm({ open: false, type: null, index: null, item: null });
    toast.success(`Audio ${deleteConfirm.type} removed`);
    // Immediate save after delete
    setTimeout(saveNow, 0);
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Music className="h-5 w-5 text-blue-400" />
            Audio
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overview */}
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Overview</Label>
          <Textarea
            value={audioOverview}
            onChange={(e) => handleOverviewChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Audio system overview..."
            rows={3}
            className="bg-background/50"
            data-testid="audio-overview-input"
          />
        </div>

        {/* Audio Inputs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Audio Inputs</Label>
            <Button type="button" variant="outline" size="sm" onClick={addAudioInput}>
              <Plus className="h-4 w-4 mr-1" /> Add Input
            </Button>
          </div>
          {audioInputs.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleInputDragEnd}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-[60px]">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={audioInputs.filter(i => i?.id).map(i => i.id)} strategy={verticalListSortingStrategy}>
                      {audioInputs.filter(i => i?.id).map((input, index) => (
                        <SortableRow key={input.id} id={input.id} className="border-border/50">
                          <TableCell className="w-[60px] text-center font-mono text-muted-foreground">
                            {input.input_number || index + 1}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.description || ''}
                              onChange={(e) => updateAudioInput(index, 'description', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Description"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.location || ''}
                              onChange={(e) => updateAudioInput(index, 'location', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Location"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={input.notes || ''}
                              onChange={(e) => updateAudioInput(index, 'notes', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Notes"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => copyAudioInput(index)} className="hover:bg-muted" title="Copy row">
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDelete('input', index)} className="hover:bg-destructive/10" title="Delete row">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            </DndContext>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No audio inputs. Click "Add Input" to add one.</p>
          )}
        </div>

        {/* Audio Outputs */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Audio Outputs</Label>
            <Button type="button" variant="outline" size="sm" onClick={addAudioOutput}>
              <Plus className="h-4 w-4 mr-1" /> Add Output
            </Button>
          </div>
          {audioOutputs.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleOutputDragEnd}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-[60px]">#</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={audioOutputs.filter(o => o?.id).map(o => o.id)} strategy={verticalListSortingStrategy}>
                      {audioOutputs.filter(o => o?.id).map((output, index) => (
                        <SortableRow key={output.id} id={output.id} className="border-border/50">
                          <TableCell className="w-[60px] text-center font-mono text-muted-foreground">
                            {output.output_number || index + 1}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.description || ''}
                              onChange={(e) => updateAudioOutput(index, 'description', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Description"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.location || ''}
                              onChange={(e) => updateAudioOutput(index, 'location', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Location"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={output.notes || ''}
                              onChange={(e) => updateAudioOutput(index, 'notes', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Notes"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => copyAudioOutput(index)} className="hover:bg-muted" title="Copy row">
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDelete('output', index)} className="hover:bg-destructive/10" title="Delete row">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            </DndContext>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No audio outputs. Click "Add Output" to add one.</p>
          )}
        </div>

        {/* Intercom */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Headphones className="h-4 w-4" />
              Intercom
            </Label>
            <Button type="button" variant="outline" size="sm" onClick={addAudioIntercom}>
              <Plus className="h-4 w-4 mr-1" /> Add Intercom
            </Button>
          </div>
          {audioIntercoms.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleIntercomDragEnd}>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border/50">
                      <TableHead className="w-8"></TableHead>
                      <TableHead className="w-[60px]">#</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead className="w-[120px]">Type</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Channels</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <SortableContext items={audioIntercoms.filter(i => i?.id).map(i => i.id)} strategy={verticalListSortingStrategy}>
                      {audioIntercoms.filter(i => i?.id).map((intercom, index) => (
                        <SortableRow key={intercom.id} id={intercom.id} className="border-border/50">
                          <TableCell className="w-[60px] text-center font-mono text-muted-foreground">
                            {intercom.com_number || index + 1}
                          </TableCell>
                          <TableCell>
                            <Input
                              value={intercom.user || ''}
                              onChange={(e) => updateAudioIntercom(index, 'user', e.target.value)}
                              onBlur={saveNow}
                              placeholder="User"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Select
                              value={intercom.type || 'none'}
                              onValueChange={(value) => {
                                updateAudioIntercom(index, 'type', value === 'none' ? '' : value);
                                setTimeout(saveNow, 0);
                              }}
                            >
                              <SelectTrigger className="bg-background/50 h-8">
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Select type</SelectItem>
                                <SelectItem value="Wired">Wired</SelectItem>
                                <SelectItem value="Wireless">Wireless</SelectItem>
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Input
                              value={intercom.location || ''}
                              onChange={(e) => updateAudioIntercom(index, 'location', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Location"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={intercom.channels || ''}
                              onChange={(e) => updateAudioIntercom(index, 'channels', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Channels"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              value={intercom.notes || ''}
                              onChange={(e) => updateAudioIntercom(index, 'notes', e.target.value)}
                              onBlur={saveNow}
                              placeholder="Notes"
                              className="bg-background/50 h-8"
                            />
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="icon" onClick={() => copyAudioIntercom(index)} className="hover:bg-muted" title="Copy row">
                                <Copy className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => confirmDelete('intercom', index)} className="hover:bg-destructive/10" title="Delete row">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </TableCell>
                        </SortableRow>
                      ))}
                    </SortableContext>
                  </TableBody>
                </Table>
              </div>
            </DndContext>
          ) : (
            <p className="text-muted-foreground text-center py-4 text-sm">No intercom entries. Click "Add Intercom" to add one.</p>
          )}
        </div>

        {/* Notes */}
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Additional Notes</Label>
          <Textarea
            value={audioNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Additional audio notes..."
            rows={3}
            className="bg-background/50"
            data-testid="audio-notes-input"
          />
        </div>
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, type: null, index: null, item: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Audio {deleteConfirm.type === 'input' ? 'Input' : deleteConfirm.type === 'output' ? 'Output' : 'Intercom'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this audio {deleteConfirm.type}?
              {deleteConfirm.item && (
                <span className="block mt-2 font-medium text-foreground">
                  #{deleteConfirm.item.input_number || deleteConfirm.item.output_number || deleteConfirm.item.com_number || '?'}: {deleteConfirm.item.description || deleteConfirm.item.user || 'No description'}
                </span>
              )}
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={removeItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableAudioTab;
